import { castArray, ii } from 'vtils'
import { currentMall } from '@/mall'

export interface UniGoods {
  /** 是否统一商品 */
  isUniGoods: boolean
  /** 来源 */
  from: 'group_buying' | 'seckill' | 'mall' | 'search' | 'resource'
  /** 商品 ID */
  id: string
  /** 封面图 */
  cover: string
  /** 名称 */
  name: string
  /** 库存 */
  stock: number
  /** 库存预警 */
  alert: boolean
  /** 已售罄 */
  sold: boolean
  /** 成本价 */
  cost: number
  /** 价格 */
  price: number
  /** 销量 */
  sales: number
  /** 资源位 ID */
  resourcePositionId: string
  /** 资源位名称 */
  resourcePositionName: string
  /** 资源 ID */
  resourceId: string
  /** 资源名称 */
  resourceName: string
  /** 是否是专题（若是专题跳转不一样） */
  topic?: boolean
  /** 跳转地址（若存在则跳该地址） */
  url?: string
  /** 是否仅显示封面 */
  onlyCover?: boolean
  /** 是否秒杀商品 */
  seckill?: boolean
  /** 秒杀状态 */
  seckillStatus?: 'pending' | 'running' | 'ended'
  /** 秒杀限制 */
  seckillLimit?: string
  /** 秒杀进度（100表示） */
  seckillProgress?: number
  /** 秒杀是否成功 */
  seckillSuccess?: boolean
  /** 商品标签 */
  tags: string[]
  /** 优惠券列表 */
  coupons: string[]
  /** 原始数据 */
  raw: any
}

/**
 * 转换为统一商品数据。
 */
export function makeUniGoods<T extends Record<any, any>>(data: T[]): UniGoods[]

/**
 * 转换为统一商品数据。
 */
export function makeUniGoods<T extends Record<any, any>>(data: T): UniGoods

/**
 * 转换为统一商品数据。
 */
export function makeUniGoods(data: any): UniGoods | UniGoods[] {
  const isArray = Array.isArray(data)
  const goods = castArray(data)
    .map<UniGoods>(item =>
      item.isUniGoods
        ? // 统一商品数据
          item
        : item.groupPrice != null
        ? // 拼团商品数据
          {
            isUniGoods: true,
            from: 'group_buying',
            id: item.commodityId,
            cover: item.commodityImage,
            name: item.commodityName,
            stock: item.surplusStock,
            alert: false,
            sold: item.surplusStock === 0,
            cost: item.dashPrice,
            price: item.groupPrice,
            sales: 0,
            resourceId: item.commodityId,
            resourceName: item.commodityName,
            resourcePositionId: '',
            resourcePositionName: '',
            tags: [],
            coupons: [],
            raw: item,
          }
        : item.seckillPrice != null
        ? // 秒杀商品数据
          {
            isUniGoods: true,
            from: 'seckill',
            id: item.commodityId,
            cover: item.commodityCoverImg,
            name: item.commodityTitle,
            stock: item.totalStock,
            alert: false,
            // 仅进行中的且剩余库存为0或进度为100%时已售罄
            sold:
              item.status === 'running' &&
              (item.surplusStock === 0 || item.progress === 100),
            cost: item.dashPrice,
            price: item.seckillPrice,
            sales: 0,
            resourceId: item.commodityId,
            resourceName: item.commodityTitle,
            resourcePositionId: '',
            resourcePositionName: '',
            seckill: true,
            // 需业务自行注入
            seckillStatus: item.status || 'none',
            seckillLimit:
              item.levelLimit === 0 || !item.levelLimitName
                ? ''
                : `限${item.levelLimitName}会员参与`,
            seckillProgress: item.progress,
            // TODO
            seckillSuccess: false,
            tags: [],
            coupons: [],
            raw: item,
          }
        : item.commodityId != null
        ? // 商城中台数据
          ii(() => {
            item.commodityTotalStock =
              item.commodityTotalStock ??
              // 兼容专题商品列表数据
              item.surplusStock
            return {
              isUniGoods: true,
              from: 'mall',
              id: item.commodityId,
              cover: item.coverPic,
              name: item.commodityTitle,
              stock: item.commodityTotalStock,
              alert:
                item.validFlag != null
                  ? // 我的收藏没有具体库存，总是为 false
                    false
                  : item.commodityTotalStock <= item.stockAlertCount,
              sold:
                item.validFlag != null
                  ? // 兼容我的收藏接口数据 1=有货，0=无货
                    item.validFlag === 0
                  : item.commodityTotalStock === 0,
              cost: item.dashPrice,
              price: item.price,
              sales: item.sales,
              resourceId: item.commodityResourceId,
              resourceName: item.commodityTitle,
              resourcePositionId: '',
              resourcePositionName: '',
              tags: [
                // '包邮',
                item.postType === 1 && '包邮',
                item.saleAfterServiceType?.includes(0) && '七天无理由退货',
              ].filter(Boolean),
              coupons: [
                // '满20减5',
                // '满100减34',
                // '满2333330减5',
                // '满100减343',
                ...(item.couponList || []),
              ],
              raw: item,
            }
          })
        : item.stateId != null
        ? // 资源位数据
          ii((): UniGoods => {
            /**
             * 资源类型。
             *
             * 01商品 02功能 03专题 04专区 05广告 06商品规格 07资讯
             */
            const type: 1 | 2 | 3 | 4 | 5 | 6 | 7 = Number(item.resType) as any
            const targetUrl = (item.targetUrl ?? item.routeUrl) || ''
            const isInternalTopic = !targetUrl && type === 3
            const isInternalGoods =
              type === 1 &&
              (item.regionId === currentMall.mall_resource_flag_id ||
                targetUrl.includes('dcb-mall-china') ||
                targetUrl.includes('duoduo-mall'))
            const url: string = targetUrl
            const onlyCover = !isInternalGoods
            return {
              isUniGoods: true,
              from: 'resource',
              id: item.id ?? item.resourceId,
              cover: item.icon ?? item.picUrl,
              name: item.title,
              stock: 1000,
              alert: false,
              sold: false,
              cost: +(item.amt ?? item.markPrice),
              price: +(item.discountAmt ?? item.price),
              sales: +item.sales,
              resourceId: item.id ?? item.resourceId,
              resourceName: item.title,
              resourcePositionId: item.stateId,
              resourcePositionName: item.stateName,
              topic: isInternalTopic,
              url: url,
              onlyCover: onlyCover,
              tags: [],
              coupons: [],
              raw: item,
            }
          })
        : // 搜索中心数据
          {
            isUniGoods: true,
            from: 'search',
            id: item.commodityResourceId,
            cover: item.coverPic,
            name: item.commodityTitle,
            stock: item.commodityTotalStock,
            alert: item.commodityTotalStock <= item.alertLeftStock,
            sold: item.commodityTotalStock === 0,
            cost: item.dashPrice,
            price: item.price,
            sales: item.saleNum,
            resourceId: item.commodityResourceId,
            resourceName: item.commodityTitle,
            resourcePositionId: '',
            resourcePositionName: '',
            tags: [],
            coupons: [],
            raw: item,
          },
    )
    .map(item => ({
      ...item,
      cost: item.cost === item.price ? 0 : item.cost,
    }))
  return isArray ? goods : goods[0]
}
